'use client';

import { useState } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Text,
  Badge,
  IconButton,
  Switch,
  Button,
  LegacyDialog,
  Input,
  Textarea,
  Tooltip,
  Field,
} from '@terra/ui-design-system';
import {
  ArrowLeft02Icon,
  Download01Icon,
  Edit01Icon,
  FilterIcon,
  Add01Icon,
  Delete01Icon,
  Upload01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { SimpleInterceptTab } from './SimpleInterceptTab';
import type { InterceptRule } from '../../store/proxy-store';
import { useProxyStore } from '../../store/proxy-store';
import { useInterceptRulesPersistence } from '../../hooks/useInterceptRulesPersistence';
import { EmptyState } from '../EmptyState';
import { useTabScopedInterceptFilters } from '../../store/tab/interceptTabScopedFilterStore';

export function InterceptTabWrapper() {
  const { panelView, setPanelView } = useTabScopedInterceptFilters();
  const {
    settings,
    updateSettings,
    addInterceptRule,
    updateInterceptRule,
    removeInterceptRule,
  } = useProxyStore();
  const { schedulePersistRulesToServer } = useInterceptRulesPersistence();
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<InterceptRule | null>(null);
  const [newRule, setNewRule] = useState<Partial<InterceptRule>>({
    name: '',
    urlPattern: '',
    methods: ['GET'],
    description: '',
    enabled: true,
  });
  const handleToggleIntercept = (enabled: boolean) => {
    updateSettings({ interceptEnabled: enabled });
  };

  const handleAddRule = () => {
    const name = newRule.name?.trim();
    const urlPattern = newRule.urlPattern?.trim();

    if (!name || !urlPattern) {
      return;
    }

    const rule: InterceptRule = {
      id: crypto.randomUUID(),
      name,
      urlPattern,
      methods: newRule.methods || ['GET'],
      description: newRule.description,
      enabled: newRule.enabled ?? true,
    };

    if (editingRule) {
      updateInterceptRule(editingRule.id, { ...rule, id: editingRule.id });
      setEditingRule(null);
    } else {
      addInterceptRule(rule);
    }

    schedulePersistRulesToServer();
    resetNewRule();
    setIsRuleDialogOpen(false);
  };

  const resetNewRule = () => {
    setNewRule({
      name: '',
      urlPattern: '',
      methods: ['GET'],
      description: '',
      enabled: true,
    });
  };

  const handleEditRule = (rule: InterceptRule) => {
    setEditingRule(rule);
    setNewRule({ ...rule });
    setIsRuleDialogOpen(true);
  };

  const handleExportRules = () => {
    try {
      const rulesJson = JSON.stringify(settings.interceptRules || [], null, 2);
      const blob = new Blob([rulesJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = 'terra-intercept-rules.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleImportRules = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedRules = JSON.parse(content);

        updateSettings({ interceptRules: importedRules });
        schedulePersistRulesToServer();
      } catch (error) {
        console.error('Import failed:', error);
      }
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const enabledRules = (settings.interceptRules || []).filter(
    (rule) => rule.enabled,
  );

  // Queue View (SimpleInterceptTab with button)
  if (panelView === 'queue') {
    return <SimpleInterceptTab onOpenRules={() => setPanelView('rules')} />;
  }

  // Rules View
  return (
    <>
      <VStack h="full" w="full" gap="0" overflow="hidden">
        {/* Toolbar */}
        <HStack
          w="full"
          p="1"
          borderBottomWidth="1px"
          borderColor="border.default"
          bg="bg.subtle"
          gap="1"
          alignItems="center"
          flexShrink={0}
        >
          <Tooltip title="Back to Queue">
            <IconButton
              size="xs"
              variant="ghost"
              onClick={() => setPanelView('queue')}
              aria-label="Back to Queue"
            >
              <ArrowLeft02Icon size={14} />
            </IconButton>
          </Tooltip>

          <Badge variant="emphasis" size="sm">
            {enabledRules.length}/{(settings.interceptRules || []).length}
          </Badge>
          <Text fontSize="xs" color="fg.muted">
            Active
          </Text>

          <Box flex="1" />

          <input
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            id="import-intercept-rules"
            onChange={handleImportRules}
          />
          <Tooltip title="Import Rules">
            <IconButton
              size="xs"
              variant="ghost"
              onClick={() =>
                document.getElementById('import-intercept-rules')?.click()
              }
              aria-label="Import Rules"
            >
              <Upload01Icon size={12} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export Rules">
            <IconButton
              size="xs"
              variant="ghost"
              onClick={handleExportRules}
              aria-label="Export Rules"
            >
              <Download01Icon size={12} />
            </IconButton>
          </Tooltip>

          <Text fontSize="xs" color="fg.muted">
            Engine
          </Text>
          <Switch
            size="sm"
            checked={settings.interceptEnabled}
            onCheckedChange={(details) =>
              handleToggleIntercept(details.checked)
            }
          />

          <IconButton
            size="xs"
            variant="ghost"
            onClick={() => {
              resetNewRule();
              setEditingRule(null);
              setIsRuleDialogOpen(true);
            }}
            aria-label="Add Rule"
          >
            <Add01Icon size={12} />
          </IconButton>
        </HStack>

        {/* Content */}
        <Box flex="1" overflow="auto" w="full">
          {(settings.interceptRules || []).length === 0 ? (
            <EmptyState
              icon={FilterIcon}
              title="No Intercept Rules"
              description="Create rules to intercept specific HTTP requests for inspection and modification"
            />
          ) : (
            <VStack gap="2" p="4" alignItems="stretch">
              {(settings.interceptRules || []).map((rule) => (
                <Box
                  key={rule.id}
                  p="3"
                  borderWidth="1px"
                  borderColor="border.subtle"
                  borderRadius="md"
                  bg="bg.default"
                >
                  <HStack justify="space-between" alignItems="flex-start">
                    <VStack gap="1" alignItems="flex-start" flex="1">
                      <HStack gap="2" alignItems="center">
                        <Text fontSize="sm" fontWeight="medium">
                          {rule.name}
                        </Text>
                        <Badge variant="emphasis" size="sm">
                          {rule.enabled ? 'Active' : 'Disabled'}
                        </Badge>
                      </HStack>

                      <Text fontSize="xs" color="fg.muted">
                        {rule.urlPattern}
                      </Text>

                      {rule.description && (
                        <Text fontSize="xs" color="fg.muted">
                          {rule.description}
                        </Text>
                      )}

                      <HStack gap="1" flexWrap="wrap">
                        {rule.methods.map((method) => (
                          <Badge key={method} size="sm" variant="outline">
                            {method}
                          </Badge>
                        ))}
                      </HStack>
                    </VStack>

                    <HStack gap="1">
                      <Switch
                        size="sm"
                        checked={rule.enabled}
                        onCheckedChange={(details) => {
                          updateInterceptRule(rule.id, {
                            enabled: details.checked,
                          });
                          schedulePersistRulesToServer();
                        }}
                      />
                      <IconButton
                        size="xs"
                        variant="ghost"
                        onClick={() => handleEditRule(rule)}
                        aria-label="Edit Rule"
                      >
                        <Edit01Icon size={14} />
                      </IconButton>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          removeInterceptRule(rule.id);
                          schedulePersistRulesToServer();
                        }}
                        aria-label="Delete Rule"
                      >
                        <Delete01Icon size={14} />
                      </IconButton>
                    </HStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </VStack>

      {/* Rule Dialog */}
      <LegacyDialog.Root
        open={isRuleDialogOpen}
        onOpenChange={(details) => setIsRuleDialogOpen(details.open)}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="xl">
            <VStack gap="4" p="4">
              <VStack gap="1" alignItems="flex-start" w="full">
                <LegacyDialog.Title>
                  {editingRule
                    ? 'Edit Intercept Rule'
                    : 'Create Intercept Rule'}
                </LegacyDialog.Title>
                <LegacyDialog.Description>
                  Define which HTTP requests should be intercepted
                </LegacyDialog.Description>
              </VStack>

              <LegacyDialog.CloseTrigger
                asChild
                position="absolute"
                top="2"
                right="2"
              >
                <IconButton aria-label="Close Dialog" variant="ghost" size="xs">
                  <Cancel01Icon size={14} />
                </IconButton>
              </LegacyDialog.CloseTrigger>

              <VStack gap="3" alignItems="flex-start" w="full">
                <Field.Root>
                  <Field.Label fontSize="sm">Rule Name</Field.Label>
                  <Input
                    size="sm"
                    value={newRule.name || ''}
                    onChange={(e) =>
                      setNewRule({ ...newRule, name: e.target.value })
                    }
                    placeholder="e.g., API Requests"
                  />
                  <Field.HelperText fontSize="xs">
                    Descriptive name for this rule
                  </Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">URL Pattern</Field.Label>
                  <Input
                    size="sm"
                    value={newRule.urlPattern || ''}
                    onChange={(e) =>
                      setNewRule({ ...newRule, urlPattern: e.target.value })
                    }
                    placeholder="e.g., *.example.com/api/*"
                  />
                  <Field.HelperText fontSize="xs">
                    Use wildcards (*) to match multiple URLs
                  </Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">HTTP Methods</Field.Label>
                  <HStack gap="2" flexWrap="wrap">
                    {[
                      'GET',
                      'POST',
                      'PUT',
                      'DELETE',
                      'PATCH',
                      'HEAD',
                      'OPTIONS',
                    ].map((method) => (
                      <Button
                        key={method}
                        size="xs"
                        variant={
                          newRule.methods?.includes(method)
                            ? 'solid'
                            : 'outline'
                        }
                        onClick={() => {
                          const currentMethods = newRule.methods || [];

                          if (currentMethods.includes(method)) {
                            setNewRule({
                              ...newRule,
                              methods: currentMethods.filter(
                                (m) => m !== method,
                              ),
                            });
                          } else {
                            setNewRule({
                              ...newRule,
                              methods: [...currentMethods, method],
                            });
                          }
                        }}
                      >
                        {method}
                      </Button>
                    ))}
                  </HStack>
                  <Field.HelperText fontSize="xs">
                    Select which HTTP methods to intercept
                  </Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm">
                    Description (Optional)
                  </Field.Label>
                  <Textarea
                    size="sm"
                    value={newRule.description || ''}
                    onChange={(e) =>
                      setNewRule({ ...newRule, description: e.target.value })
                    }
                    placeholder="Describe what this rule intercepts..."
                    rows={2}
                  />
                </Field.Root>

                <Switch
                  size="sm"
                  checked={newRule.enabled ?? true}
                  onCheckedChange={(details) =>
                    setNewRule({ ...newRule, enabled: details.checked })
                  }
                >
                  Enable rule immediately
                </Switch>
              </VStack>

              <HStack gap="2" justify="flex-end" w="full">
                <LegacyDialog.CloseTrigger asChild>
                  <Button variant="outline" size="xs">
                    Cancel
                  </Button>
                </LegacyDialog.CloseTrigger>
                <Button size="xs" onClick={handleAddRule}>
                  {editingRule ? 'Update Rule' : 'Create Rule'}
                </Button>
              </HStack>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}
